import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlotCard } from "../PlotCard";
import { useTimezone, utcToLocalNaive } from "../../lib/datetime";

interface Props {
  fromIso: string;
  toIso: string;
}

/**
 * Scatter of (datetime, cutterhead chainage at firing time) coloured by
 * box number. Mirrors `Overview_plot.png`. Lets the user see which box
 * was active at which ground position over the course of the drive.
 */
export function BoxSelectionScatter({ fromIso, toIso }: Props) {
  const tz = useTimezone();
  const series = useQuery({
    queryKey: ["lubrication", "series", fromIso, toIso],
    queryFn: () => api.lubrication.series({ from: fromIso, to: toIso }),
    refetchInterval: 60_000,
  });

  const data = useMemo(() => {
    const records = (series.data?.records ?? []).filter(
      (r) => r.chainageM != null && r.boxNo != null,
    );
    return [
      {
        x: records.map((r) => utcToLocalNaive(r.ts, tz)),
        y: records.map((r) => r.chainageM),
        type: "scatter",
        mode: "markers",
        marker: {
          size: 4,
          color: records.map((r) => r.boxNo),
          colorscale: "Viridis",
          colorbar: { title: { text: "Box nr" }, thickness: 10 },
        },
        hovertemplate:
          "Box %{marker.color} · %{x|%-d %b · %H:%M} · %{y:.1f} m<extra></extra>",
      },
    ] as Record<string, unknown>[];
  }, [series.data, tz]);

  return (
    <PlotCard
      title="Box selection over time"
      subtitle="Cutterhead chainage at firing, coloured by active box"
      data={data as never}
      height={420}
      uirevision={`box-selection-${fromIso}`}
      layout={{
        margin: { l: 56, r: 16, t: 16, b: 36 },
        xaxis: { tickformat: "%-d %b", hoverformat: "%-d %b %Y · %H:%M" },
        yaxis: { title: { text: "Chainage (m)" } },
        showlegend: false,
      }}
    />
  );
}
